/**
 * Module quản lý đọc truyện ngụy trang đa nền tảng:
 * 1. Google Sheets (Bảng tính kế toán)
 * 2. Gmail Web (Hộp thư công việc)
 * 3. Zalo Web (Nhóm chat nội bộ)
 */
import { FAKE_STATUSES, FAKE_DEPARTMENTS, DEFAULT_SOURCE } from '../config/constants.js';
import { ApiClient } from '../services/apiClient.js';
import { StorageService } from '../services/storageService.js';
import { UrlService } from '../services/urlService.js';
import { AppearanceService } from '../services/appearanceService.js';
import { sessionManager } from '../core/workspace/sessionManager.js';

// Danh sách tên đồng nghiệp ngụy trang trong nhóm chat Zalo
const FAKE_COWORKERS = [
  'Nguyễn Hoàng Nam (P. Kế toán)',
  'Trần Thu Hà (Khối Vận hành)',
  'Lê Quốc Tuấn (Kỹ thuật & IT)',
  'Phạm Mai Anh (Phòng Nhân sự)',
  'Vũ Đức Thắng (Ban Kiểm toán)',
];

export class StoryReaderModule {
  constructor(spreadsheet) {
    this.spreadsheet = spreadsheet;
    this.currentStory = {
      source: DEFAULT_SOURCE,
      storyId: '',
      chapterId: '',
      truyenTitle: '',
      chapterTitle: '',
      prevChapterId: null,
      nextChapterId: null,
    };

    // Lưu dữ liệu chương tải gần nhất để chuyển đổi qua lại giữa các theme tức thì
    this.lastLoadedData = null;
    this.storyChapters = new Map();
    this.pendingStoryDetails = new Map();
    this.currentSentenceIndex = 0;

    // Lưu danh sách các tab chương đã mở trong Google Sheets
    this.openedTabs = [];

    // DOM Elements - Google Sheets
    this.docTitleInput = document.getElementById('doc-title-input');
    this.currentStoryBadge = document.getElementById('current-story-badge');
    this.sheetTabsList = document.getElementById('sheet-tabs-list');
    this.btnPrevChap = document.getElementById('btn-prev-chap');
    this.btnNextChap = document.getElementById('btn-next-chap');
    this.btnAddSheet = document.getElementById('btn-add-sheet');

    // DOM Elements - Gmail
    this.gmailSubject = document.getElementById('gmail-email-subject');
    this.gmailBody = document.getElementById('gmail-email-body');
    this.gmailPageInfo = document.getElementById('gmail-page-info');
    this.gmailBtnPrev = document.getElementById('gmail-btn-prev');
    this.gmailBtnNext = document.getElementById('gmail-btn-next');
    this.gmailBtnPrevBottom = document.getElementById('gmail-btn-prev-bottom');
    this.gmailBtnNextBottom = document.getElementById('gmail-btn-next-bottom');

    // DOM Elements - Zalo
    this.zaloChatTitle = document.getElementById('zalo-chat-header-title');
    this.zaloMessagesArea = document.getElementById('zalo-messages-area');
    this.zaloListGroupTitle = document.getElementById('zalo-list-group-title');
    this.zaloListGroupPreview = document.getElementById('zalo-list-group-preview');
    this.zaloBtnPrev = document.getElementById('zalo-btn-prev');
    this.zaloBtnNext = document.getElementById('zalo-btn-next');

    this.bindEvents();
  }

  /**
   * Khởi tạo bảng tính ở trạng thái trống mặc định (khi chưa chọn truyện)
   */
  initEmptySheet() {
    this.spreadsheet.clear();

    if (this.docTitleInput) this.docTitleInput.value = 'Bang_tinh_chua_dat_ten.xlsx';
    if (this.currentStoryBadge) this.currentStoryBadge.textContent = 'Bảng tính trống (Nhấn F4 đổi giao diện / Ctrl+K tìm truyện)';
    if (this.btnPrevChap) this.btnPrevChap.disabled = true;
    if (this.btnNextChap) this.btnNextChap.disabled = true;
    if (this.spreadsheet.formulaInput) this.spreadsheet.formulaInput.value = '';

    if (this.sheetTabsList) {
      this.sheetTabsList.innerHTML = `
        <div class="sheet-tab active" title="Trang tính Sheet1">
          <span>Sheet1</span>
          <div class="sheet-tab-arrow"></div>
        </div>
      `;
    }

    this.spreadsheet.totalRows = 100;
    this.spreadsheet.renderRowHeaders();
    this.spreadsheet.renderGrid();
    this.spreadsheet.selectCell(1, 'A');

    // Đồng bộ Favicon và Title ở trạng thái bảng tính trống mặc định
    const currentTheme = localStorage.getItem('fake_disguise_theme') || 'sheets';
    AppearanceService.updateAppearance(currentTheme, null);
  }

  bindEvents() {
    // 1. Điều hướng trên Google Sheets
    if (this.btnPrevChap) {
      this.btnPrevChap.addEventListener('click', () => this.navigateChapter('prev'));
    }
    if (this.btnNextChap) {
      this.btnNextChap.addEventListener('click', () => this.navigateChapter('next'));
    }
    if (this.btnAddSheet) {
      this.btnAddSheet.addEventListener('click', () => {
        if (this.currentStory.nextChapterId) {
          this.navigateChapter('next');
        } else {
          alert('Đã là chương mới nhất của truyện!');
        }
      });
    }

    // 2. Điều hướng trên Gmail
    const prevAction = () => this.navigateChapter('prev');
    const nextAction = () => this.navigateChapter('next');

    if (this.gmailBtnPrev) this.gmailBtnPrev.addEventListener('click', prevAction);
    if (this.gmailBtnPrevBottom) this.gmailBtnPrevBottom.addEventListener('click', prevAction);
    if (this.gmailBtnNext) this.gmailBtnNext.addEventListener('click', nextAction);
    if (this.gmailBtnNextBottom) this.gmailBtnNextBottom.addEventListener('click', nextAction);

    // 3. Điều hướng trên Zalo
    if (this.zaloBtnPrev) this.zaloBtnPrev.addEventListener('click', prevAction);
    if (this.zaloBtnNext) this.zaloBtnNext.addEventListener('click', nextAction);

    // 4. Phím tắt chuyển chương toàn cục (Alt + Mũi tên Trái / Phải hoặc PageUp / PageDown)
    window.addEventListener('keydown', (e) => {
      if (document.activeElement.tagName === 'INPUT' || document.activeElement.tagName === 'TEXTAREA') return;

      if ((e.key === 'PageDown' || (e.altKey && e.key === 'ArrowRight')) && this.currentStory.nextChapterId) {
        e.preventDefault();
        this.navigateChapter('next');
      }
      if ((e.key === 'PageUp' || (e.altKey && e.key === 'ArrowLeft')) && this.currentStory.prevChapterId) {
        e.preventDefault();
        this.navigateChapter('prev');
      }
    });
  }

  navigateChapter(direction) {
    const targetChapterId = direction === 'prev' ? this.currentStory.prevChapterId : this.currentStory.nextChapterId;
    if (targetChapterId) {
      this.loadChapter(this.currentStory.source, this.currentStory.storyId, targetChapterId, true);
    }
  }

  /**
   * Tải nội dung chương truyện theo source, storyId và chapterId
   */
  async loadChapter(source, storyId, chapterId, updateUrl = true, pushState = true) {
    if (!storyId || !chapterId) {
      console.log('[storyReader] Chưa chọn truyện hoặc chương.');
      this.initEmptySheet();
      return;
    }

    const src = source || this.currentStory.source || DEFAULT_SOURCE;
    const sId = String(storyId).trim();
    const cId = String(chapterId).trim();

    if (this.spreadsheet && this.spreadsheet.formulaInput) {
      this.spreadsheet.formulaInput.value = 'Đang tải dữ liệu trang tính từ máy chủ...';
    }

    try {
      const res = await ApiClient.getChapterContent(sId, cId, src);
      if (res.success && res.data) {
        this.populateData(res.data);

        if (updateUrl) {
          UrlService.setReading(res.data.source || src, sId, cId, pushState);
        }
      } else {
        alert('Lỗi tải chương: ' + (res.message || 'Không xác định'));
      }
    } catch (err) {
      alert('Lỗi kết nối máy chủ: ' + err.message);
    }
  }

  /**
   * Đổ dữ liệu chương đã được làm sạch vào tất cả các giao diện
   * @param {object} data 
   */
  populateData(data) {
    this.lastLoadedData = data;

    this.currentStory.source = data.source || DEFAULT_SOURCE;
    this.currentStory.storyId = data.story_id;
    this.currentStory.chapterId = data.chapter_id;
    this.currentStory.truyenTitle = data.truyen_title || 'Truyện';
    this.currentStory.chapterTitle = data.chapter_title || 'Chương';
    this.currentStory.prevChapterId = data.prev_chapter_id;
    this.currentStory.nextChapterId = data.next_chapter_id;

    // Đổ dữ liệu vào Google Sheets
    this.populateSheets(data);

    // Đổ dữ liệu vào Gmail Web
    this.populateEmail(data);

    // Đổ dữ liệu vào Zalo Web
    this.populateZalo(data);

    // Lưu vị trí đọc vào LocalStorage
    StorageService.saveLastReadUrl(`${this.currentStory.source}:${data.story_id}:${data.chapter_id}`);

    // Lưu ngay chương hiện tại; tải mục lục nền nếu mở bằng deep link.
    this.updateSentenceProgress(0);
    void this.refreshChapterProgress(data);

    // Đồng bộ Favicon và Title trên trình duyệt theo giao diện và chương truyện hiện tại
    const currentTheme = localStorage.getItem('fake_disguise_theme') || 'sheets';
    AppearanceService.updateAppearance(currentTheme, data);

    // Tự động nạp tài liệu vào TTSEngine để sẵn sàng phát âm thanh
    if (this.ttsEngine) {
      this.ttsEngine.loadDocument({
        id: data.chapter_id,
        title: `${data.truyen_title} - ${data.chapter_title}`,
        paragraphs: data.paragraphs || [],
      });
    }
  }

  /**
   * Đổ lại dữ liệu vào giao diện cụ thể khi người dùng chuyển theme
   */
  renderToTheme(themeName, data) {
    if (!data) return;
    if (themeName === 'sheets') this.populateSheets(data);
    else if (themeName === 'email') this.populateEmail(data);
    else if (themeName === 'zalo') this.populateZalo(data);
    this.renderChapterProgress();
  }

  setStoryChapters(source, storyId, chapters) {
    if (!Array.isArray(chapters) || chapters.length === 0) return;
    this.storyChapters.set(JSON.stringify([source, storyId]), chapters);
  }

  getChapterProgress() {
    const { source, storyId, chapterId } = this.currentStory;
    const chapters = this.storyChapters.get(JSON.stringify([source, storyId])) || [];
    const index = chapters.findIndex(chapter => String(chapter.chapter_id) === String(chapterId));
    return { chapterNumber: index >= 0 ? index + 1 : 0, totalChapters: chapters.length };
  }

  async refreshChapterProgress(data) {
    const source = data.source || DEFAULT_SOURCE;
    const key = JSON.stringify([source, data.story_id]);
    try {
      if (!this.storyChapters.has(key)) {
        if (!this.pendingStoryDetails.has(key)) {
          const request = ApiClient.getStoryDetail(data.story_id, source).then(res => {
            if (res.success && res.data) this.setStoryChapters(source, data.story_id, res.data.chapters);
          }).finally(() => this.pendingStoryDetails.delete(key));
          this.pendingStoryDetails.set(key, request);
        }
        await this.pendingStoryDetails.get(key);
      }
      // Mục lục của truyện cũ không được ghi đè phiên đang đọc truyện mới.
      if (this.lastLoadedData !== data) return;
      this.renderChapterProgress();
      const activeSession = sessionManager.getLastActiveSession();
      if (activeSession?.type !== 'reader' || activeSession.position?.storyId !== data.story_id
        || activeSession.position?.source !== source) return;
      this.updateSentenceProgress(this.currentSentenceIndex);
      if (window.appRouter?.currentMode === 'home') window.appRouter.homeController?.refresh();
    } catch (err) {
      console.warn('[storyReader] Chưa lấy được tổng số chương:', err.message);
    }
  }

  renderChapterProgress() {
    const { chapterNumber, totalChapters } = this.getChapterProgress();
    if (!chapterNumber || !totalChapters) return;
    const percent = Math.min(100, Math.round(chapterNumber / totalChapters * 1000) / 10);
    const progress = `${chapterNumber}/${totalChapters} chương · ${percent}%`;
    const { truyenTitle, chapterTitle } = this.currentStory;
    if (this.currentStoryBadge) this.currentStoryBadge.textContent = `${truyenTitle} (${chapterTitle}) · ${progress}`;
    if (this.gmailPageInfo) this.gmailPageInfo.textContent = `${chapterTitle} · ${progress}`;
    if (this.zaloChatTitle) this.zaloChatTitle.textContent = `Team Dự án Q4 - ${truyenTitle} (${chapterTitle}) · ${progress}`;
  }

  /**
   * Cập nhật tiến độ dòng/câu đang đọc cho Session
   * @param {number} sentenceIndex Chỉ số câu đang đọc
   */
  updateSentenceProgress(sentenceIndex) {
    if (!this.lastLoadedData || !this.currentStory.storyId) return;
    this.currentSentenceIndex = sentenceIndex;
    this.renderChapterProgress();

    try {
      const curTheme = localStorage.getItem('fake_disguise_theme') || 'sheets';
      const targetAppId = (curTheme === 'email' || curTheme === 'zalo') ? curTheme : 'sheets';
      sessionManager.updateReaderSession({
        appId: targetAppId,
        source: this.currentStory.source,
        storyId: this.currentStory.storyId,
        chapterId: this.currentStory.chapterId,
        truyenTitle: this.currentStory.truyenTitle,
        chapterTitle: this.currentStory.chapterTitle,
        ...this.getChapterProgress(),
        sentenceIndex: sentenceIndex,
        totalSentences: (this.lastLoadedData.paragraphs || []).length,
        tts: {
          enabled: Boolean(this.ttsEngine?.isPlaying),
          speed: this.ttsEngine?.currentPlaybackRate || 1.0
        }
      });
    } catch (e) {}
  }

  /**
   * 1. Render dữ liệu vào Google Sheets
   */
  populateSheets(data) {
    this.spreadsheet.clear();

    if (this.docTitleInput) {
      this.docTitleInput.value = `${data.truyen_title} - ${data.chapter_title}.xlsx`;
    }
    if (this.currentStoryBadge) {
      this.currentStoryBadge.textContent = `${data.truyen_title} (${data.chapter_title})`;
    }

    if (this.btnPrevChap) this.btnPrevChap.disabled = !data.prev_chapter_id;
    if (this.btnNextChap) this.btnNextChap.disabled = !data.next_chapter_id;

    this.updateSheetTabs(data);

    const paragraphs = data.paragraphs || [];
    paragraphs.forEach((line, index) => {
      const r = index + 1;
      // Cột A trực tiếp chứa nội dung câu văn truyện (STT đã có sẵn từ Row Header 1, 2, 3... của Google Sheets)
      this.spreadsheet.setCellValue(r, 'A', line);
      this.spreadsheet.setCellValue(r, 'B', FAKE_STATUSES[index % FAKE_STATUSES.length]);
      this.spreadsheet.setCellValue(r, 'C', FAKE_DEPARTMENTS[index % FAKE_DEPARTMENTS.length]);

      const revVal = (index * 1250000 + 15000000);
      const costVal = (index * 650000 + 8000000);
      const diffVal = revVal - costVal;

      this.spreadsheet.setCellValue(r, 'D', revVal.toLocaleString('vi-VN'));
      this.spreadsheet.setCellValue(r, 'E', costVal.toLocaleString('vi-VN'));
      this.spreadsheet.setCellValue(r, 'F', diffVal.toLocaleString('vi-VN'));
    });

    this.spreadsheet.totalRows = Math.max(150, paragraphs.length + 20);
    this.spreadsheet.renderRowHeaders();
    this.spreadsheet.renderGrid();
    this.spreadsheet.selectCell(1, 'A');
    this.spreadsheet.gridCells.scrollTop = 0;
    this.spreadsheet.gridCells.scrollLeft = 0;
  }

  /**
   * 2. Render dữ liệu vào Gmail Web
   */
  populateEmail(data) {
    if (this.gmailSubject) {
      this.gmailSubject.innerHTML = `
        <span>[BÁO CÁO TIẾN ĐỘ] ${data.truyen_title} - ${data.chapter_title}</span>
        <span class="gmail-tag-inbox">Hộp thư đến</span>
      `;
    }

    if (this.gmailPageInfo) {
      this.gmailPageInfo.textContent = data.chapter_title || 'Chương hiện tại';
    }

    if (this.gmailBtnPrev) this.gmailBtnPrev.disabled = !data.prev_chapter_id;
    if (this.gmailBtnPrevBottom) this.gmailBtnPrevBottom.disabled = !data.prev_chapter_id;
    if (this.gmailBtnNext) this.gmailBtnNext.disabled = !data.next_chapter_id;
    if (this.gmailBtnNextBottom) this.gmailBtnNextBottom.disabled = !data.next_chapter_id;

    if (this.gmailBody) {
      const paragraphs = data.paragraphs || [];
      const contentHtml = paragraphs.map(p => `<p>${p}</p>`).join('');
      
      this.gmailBody.innerHTML = `
        <p><i>Kính gửi Anh/Chị Ban Điều Hành và các bộ phận liên quan,</i></p>
        <p>Dưới đây là nội dung cập nhật tiến độ công việc và số liệu đối soát chi tiết thuộc giai đoạn <b>${data.chapter_title}</b>:</p>
        <hr style="border:none;border-top:1px solid #f0f0f0;margin:16px 0;" />
        ${contentHtml}
        <hr style="border:none;border-top:1px solid #f0f0f0;margin:16px 0;" />
        <p><i>Trân trọng cảm ơn Anh/Chị đã xem xét và phản hồi.</i><br /><b>Ban Dự Án & Kế Toán Nội Bộ</b></p>
      `;

      const mainContainer = document.querySelector('.gmail-email-content');
      if (mainContainer) mainContainer.scrollTop = 0;
    }
  }

  /**
   * 3. Render dữ liệu vào Zalo Web
   */
  populateZalo(data) {
    if (this.zaloChatTitle) {
      this.zaloChatTitle.textContent = `Team Dự án Q4 - ${data.truyen_title} (${data.chapter_title})`;
    }
    if (this.zaloListGroupTitle) {
      this.zaloListGroupTitle.textContent = `Dự án Q4 - ${data.truyen_title}`;
    }
    if (this.zaloListGroupPreview && data.paragraphs && data.paragraphs.length > 0) {
      this.zaloListGroupPreview.textContent = `[${data.chapter_title}] ${data.paragraphs[0]}`;
    }

    if (this.zaloBtnPrev) this.zaloBtnPrev.disabled = !data.prev_chapter_id;
    if (this.zaloBtnNext) this.zaloBtnNext.disabled = !data.next_chapter_id;

    if (this.zaloMessagesArea) {
      const paragraphs = data.paragraphs || [];
      const messagesHtml = paragraphs.map((line, idx) => {
        const sender = FAKE_COWORKERS[idx % FAKE_COWORKERS.length];
        return `
          <div class="zalo-message-bubble">
            <div class="zalo-sender-label">${sender}</div>
            <div>${line}</div>
          </div>
        `;
      }).join('');

      this.zaloMessagesArea.innerHTML = `
        <div style="text-align:center;font-size:12px;color:#72808e;margin:10px 0;">HÔM NAY - ${data.chapter_title}</div>
        ${messagesHtml}
      `;

      this.zaloMessagesArea.scrollTop = 0;
    }
  }

  /**
   * Cập nhật danh sách Sheet Tabs trên Google Sheets
   */
  updateSheetTabs(data) {
    if (!this.sheetTabsList) return;

    const exists = this.openedTabs.some(t => t.chapterId === data.chapter_id && t.storyId === data.story_id);
    if (!exists) {
      this.openedTabs.push({
        title: data.chapter_title || 'Chương hiện tại',
        source: data.source || DEFAULT_SOURCE,
        storyId: data.story_id,
        chapterId: data.chapter_id,
      });
    }

    this.sheetTabsList.innerHTML = '';
    this.openedTabs.forEach((tab) => {
      const tabEl = document.createElement('div');
      const isActive = tab.chapterId === data.chapter_id && tab.storyId === data.story_id;
      tabEl.className = `sheet-tab ${isActive ? 'active' : ''}`;
      tabEl.innerHTML = `<span>${tab.title}</span><div class="sheet-tab-arrow"></div>`;
      tabEl.title = `Trang tính ${tab.title}`;

      tabEl.addEventListener('click', () => {
        if (!isActive) this.loadChapter(tab.source, tab.storyId, tab.chapterId, true);
      });

      this.sheetTabsList.appendChild(tabEl);
    });

    if (data.next_chapter_id) {
      const nextTabEl = document.createElement('div');
      nextTabEl.className = 'sheet-tab next-preview-tab';
      nextTabEl.textContent = 'Sheet tiếp theo →';
      nextTabEl.title = 'Bấm để mở Sheet chương tiếp theo';
      nextTabEl.addEventListener('click', () => {
        this.loadChapter(data.source, data.story_id, data.next_chapter_id, true);
      });
      this.sheetTabsList.appendChild(nextTabEl);
    }
  }
}
